/**
 * Wave 4.3 — local "fit-for-this-session" ranking.
 *
 * No Brain call needed: a session's metadata (phase coverage,
 * objectives, psych-social intent) is enough to compute a useful
 * relative score for each library drill. Higher score = better fit.
 *
 * Heuristic + transparent. Coaches don't need an explainable AI here —
 * if the order surprises them they can flip back to alphabetical.
 *
 * Components:
 *   1. PHASE FIT (0–60)   — drills matching an under-represented phase
 *      score highest. The session's phase_coverage tells us which
 *      phase needs more time.
 *   2. THEME FIT (0–40)   — count of matches between the drill's
 *      sub_themes and any word in the session's objectives /
 *      psych_social_intent (lowercased, alpha-split, length>3).
 *   3. STABILITY TIE-BREAK — fall back to alphabetical so the order
 *      is deterministic when scores tie.
 *
 * Pure function. Heavily unit-tested. The `rankByFit` wrapper preserves
 * input order via a stable sort emulator (sort by score desc,
 * tie-break by original index → name).
 */
import type { PhaseOfPlay, PhaseCoverage } from '@4cm/core/phaseOfPlay';

export interface FitDrillInput {
  id: string;
  name: string | null;
  primary_phase: PhaseOfPlay | null;
  sub_themes: string[];
}

export interface FitSessionInput {
  /** Phase coverage from the session row. Each phase = 0..100; total
   *  ≤ 100. Missing = treat as 0 (full session below average). */
  phase_coverage: Partial<PhaseCoverage> | null;
  objectives: string[] | null;
  psych_social_intent: string | null;
}

export interface FitScore {
  total: number;
  phaseFit: number;
  themeFit: number;
}

const PHASES: PhaseOfPlay[] = ['attacking', 'defending', 'transitions', 'individual'];

/** Words shorter than this are noise — articles, prepositions. */
const MIN_WORD_LEN = 4;

function pickKeywords(sources: Array<string | null | undefined>): Set<string> {
  const out = new Set<string>();
  for (const src of sources) {
    if (!src) continue;
    // Strip punctuation, split on whitespace, lowercase, keep only
    // long-enough alphabetic words.
    src
      .toLowerCase()
      .replace(/[^a-z\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length >= MIN_WORD_LEN)
      .forEach((w) => out.add(w));
  }
  return out;
}

/** Phase deficit lookup — how much each phase is "missing" from the
 *  session's coverage. Higher = a drill of this phase is more useful. */
function phaseDeficits(coverage: Partial<PhaseCoverage> | null): Record<PhaseOfPlay, number> {
  const out: Record<PhaseOfPlay, number> = {
    attacking: 0,
    defending: 0,
    transitions: 0,
    individual: 0,
  };
  if (!coverage) {
    // No coverage data — treat all phases as equally deficit-y.
    for (const p of PHASES) out[p] = 25;
    return out;
  }
  // The deficit is (100 / 4) - actual = 25 - actual when an even split
  // is the target. Floor at 0 so over-covered phases don't go negative
  // (we want them to score zero, not penalise drills of that phase).
  for (const p of PHASES) {
    const actual = Number(coverage[p] ?? 0);
    out[p] = Math.max(0, 25 - actual);
  }
  return out;
}

export function fitScoreFor(drill: FitDrillInput, session: FitSessionInput): FitScore {
  const deficits = phaseDeficits(session.phase_coverage);

  // PHASE FIT — proportional to deficit. Each unit of deficit
  // contributes 60/25 = 2.4 to the score.
  let phaseFit = 0;
  if (drill.primary_phase) {
    phaseFit = (deficits[drill.primary_phase] / 25) * 60;
  }

  // THEME FIT — Jaccard-ish: count shared keywords; each match
  // contributes up to 8, capped at 40.
  const sessionKeywords = pickKeywords([
    ...(session.objectives ?? []),
    session.psych_social_intent,
  ]);
  let matches = 0;
  for (const t of drill.sub_themes) {
    const tl = t.toLowerCase();
    if (sessionKeywords.has(tl)) {
      matches += 1;
      continue;
    }
    // Loose match — any keyword appears in the theme as a substring.
    for (const w of sessionKeywords) {
      if (tl.includes(w)) {
        matches += 1;
        break;
      }
    }
  }
  const themeFit = Math.min(40, matches * 8);

  return { total: phaseFit + themeFit, phaseFit, themeFit };
}

/**
 * Stable sort by fit-score descending. Ties broken by name alphabetical
 * (case-insensitive). Drills missing a name sort last in their tie
 * bucket. The returned array is a new copy — caller's input is never
 * mutated.
 */
export function rankByFit<T extends FitDrillInput>(
  drills: readonly T[],
  session: FitSessionInput,
): Array<T & { fitScore: FitScore }> {
  return drills
    .map((d) => ({ ...d, fitScore: fitScoreFor(d, session) }))
    .sort((a, b) => {
      if (b.fitScore.total !== a.fitScore.total) {
        return b.fitScore.total - a.fitScore.total;
      }
      const aName = (a.name ?? '').toLowerCase();
      const bName = (b.name ?? '').toLowerCase();
      if (aName && !bName) return -1;
      if (!aName && bName) return 1;
      return aName.localeCompare(bName);
    });
}
